import { useRef, useState } from "react";
import { Image as ImageIcon, X, Camera } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthImage } from "@/components/AuthImage";

const MAX_BYTES = 8 * 1024 * 1024;

// Uploads up to `max` photos (file picker or straight from the camera) and
// reports [{image_id, caption}] upward. The first photo is the cover.
export const MultiImageField = ({ images = [], onChange, max = 10, testid = "gallery" }) => {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [progress, setProgress] = useState(null);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = max - images.length;
    if (room <= 0) {
      toast.error(`Up to ${max} photos per item`);
      return;
    }
    const picked = files.slice(0, room);
    if (files.length > room) toast.error(`Only the first ${room} photos were added (${max} max)`);
    const added = [];
    for (let i = 0; i < picked.length; i += 1) {
      const file = picked[i];
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name} is over 8MB`);
        continue;
      }
      setProgress({ index: i + 1, of: picked.length, pct: 0 });
      const form = new FormData();
      form.append("file", file);
      form.append("caption", "");
      try {
        const { data } = await api.post("/images", form, {
          onUploadProgress: (evt) => setProgress({
            index: i + 1, of: picked.length,
            pct: evt.total ? Math.round((evt.loaded / evt.total) * 100) : 0,
          }),
        });
        added.push({ image_id: data.id, caption: "" });
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    }
    setProgress(null);
    if (fileRef.current) fileRef.current.value = "";
    if (cameraRef.current) cameraRef.current.value = "";
    if (added.length) {
      onChange([...images, ...added]);
      toast.success(added.length === 1 ? "Photo attached" : `${added.length} photos attached`);
    }
  };

  const removeAt = (idx) => onChange(images.filter((_, i) => i !== idx));

  const setCaption = (idx, caption) =>
    onChange(images.map((img, i) => (i === idx ? { ...img, caption } : img)));

  return (
    <div className="space-y-3 border border-dashed border-slate-300 p-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <Label className="overline text-slate-500">Photos ({images.length}/{max})</Label>
        <button
          type="button"
          data-testid={`${testid}-camera-button`}
          onClick={() => cameraRef.current?.click()}
          className="inline-flex items-center gap-1.5 border border-slate-300 px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-600 transition-colors duration-150 hover:bg-slate-100"
        >
          <Camera size={14} /> Take a photo
        </button>
      </div>

      <input
        ref={fileRef}
        data-testid={`${testid}-input`}
        type="file"
        accept="image/*"
        multiple
        onChange={upload}
        disabled={!!progress}
        className="block w-full text-xs file:mr-3 file:border file:border-slate-300 file:bg-white file:px-3 file:py-2 file:text-xs file:font-semibold file:uppercase file:tracking-wider"
      />
      <input
        ref={cameraRef}
        data-testid={`${testid}-camera-input`}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={upload}
        className="hidden"
      />

      {progress && (
        <div data-testid={`${testid}-progress`}>
          <p className="text-xs text-slate-500">
            Uploading photo {progress.index} of {progress.of} — {progress.pct}%
          </p>
          <div className="mt-1 h-1.5 w-full bg-slate-100">
            <div className="h-1.5 bg-[#002FA7] transition-all duration-150" style={{ width: `${progress.pct}%` }} />
          </div>
        </div>
      )}

      {images.length === 0 ? (
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <ImageIcon size={20} /> No photos yet — the first one becomes the cover.
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {images.map((img, idx) => (
            <div key={img.image_id} data-testid={`${testid}-item-${idx}`} className="border border-border p-2">
              <div className="relative">
                <AuthImage
                  imageId={img.image_id}
                  alt={img.caption || `Photo ${idx + 1}`}
                  className="h-24 w-full border border-border object-cover"
                  testid={`${testid}-thumb-${idx}`}
                />
                <button
                  type="button"
                  data-testid={`${testid}-remove-${idx}`}
                  onClick={() => removeAt(idx)}
                  aria-label="Remove photo"
                  className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center border border-border bg-white text-[#FF3B30]"
                >
                  <X size={12} />
                </button>
                {idx === 0 && (
                  <span className="absolute bottom-1 left-1 bg-[#002FA7] px-1.5 py-0.5 text-[0.6rem] uppercase tracking-wider text-white">
                    Cover
                  </span>
                )}
              </div>
              <Input
                data-testid={`${testid}-caption-${idx}`}
                value={img.caption || ""}
                onChange={(e) => setCaption(idx, e.target.value)}
                placeholder="Caption"
                className="mt-2 rounded-none border-slate-300 text-xs"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
